import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import UserAvatar from "@/components/UserAvatar";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { ChatCircle, WhatsappLogo, Clock, Eye } from "@phosphor-icons/react";

const HUMAN_PAGES = {
  dashboard: "Inicio", clubes: "Clubes", torneos: "Torneos", ligas: "Ligas", retas: "Retas",
  clinicas: "Clínicas", marcador: "Marcador", jugadores: "Jugadores", comunidad: "Comunidad",
  comic: "Cómic", "uso-plataforma": "Uso Plataforma", chat: "Chat", tienda: "Tienda",
  canchas: "Canchas", estadisticas: "Estadísticas", streaming: "Streaming", pagos: "Pagos",
  invitaciones: "Invitaciones", ingresos: "Ingresos", usuarios: "Usuarios", trivia: "Trivia",
  atari: "Atari", "padel-game": "Padel Game", balonpie: "Balón Pie", publicidad: "Publicidad",
  "dyd-run": "DYD Run", reportes: "Reportes", "dyd-coin": "DYD Coin", agenda: "Agenda",
  "mi-perfil": "Mi Perfil", "mi-tarjeta": "Mi Tarjeta", "carreras": "Carreras VaqueRun",
  "padel-center": "Padel Center",
};

const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function UsageDrillDown({ user, period, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = !!user;

  useEffect(() => {
    if (!open) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/usage/users/${user.user_id}?period=${period || "30d"}&top=10`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Error al cargar"))
      .finally(() => setLoading(false));
  }, [open, user?.user_id, period]);

  const openChat = () => {
    if (!user) return;
    navigate(`/dashboard/chat?to=${user.user_id}`);
  };

  const openWhatsapp = () => {
    if (!data?.user) return;
    const phone = (data.user.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Este usuario no tiene WhatsApp registrado"); return; }
    window.open(`https://wa.me/${phone}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-black border-white/10 text-white" data-testid="usage-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white font-['Barlow_Condensed'] uppercase">
            <Eye size={20} className="text-[#CCFF00]" /> Análisis de uso — {user?.name || "Usuario"}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-12 flex justify-center"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
        ) : !data ? null : (
          <div className="space-y-5">
            {/* Header user card */}
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded border border-white/10">
              <UserAvatar src={data.user.profile_photo_url} fallback={(data.user.name || "?").slice(0, 2)} className="w-14 h-14 rounded-full border border-white/20" />
              <div className="flex-1">
                <p className="text-lg font-bold text-white" data-testid="drill-user-name">{data.user.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-400">{data.user.role} · {data.user.email || "sin email"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" onClick={openChat} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-[10px] uppercase font-bold" data-testid="drill-chat-btn">
                  <ChatCircle size={14} className="mr-1" /> Chat privado
                </Button>
                <Button size="sm" variant="outline" onClick={openWhatsapp} className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-[10px] uppercase" data-testid="drill-wa-btn">
                  <WhatsappLogo size={14} className="mr-1" /> WhatsApp
                </Button>
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="drill-totals">
              <TotalCard label="Tiempo" value={fmtDuration(data.totals.seconds)} icon={Clock} accent />
              <TotalCard label="Visitas" value={data.totals.visits} />
              <TotalCard label="Sesiones" value={data.totals.sessions} />
              <TotalCard label="Días activos" value={data.totals.days_active} />
            </div>

            {/* Top pages bar chart */}
            <section>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Top pantallas usadas</p>
              {(data.top_pages || []).length === 0 ? (
                <p className="text-sm italic text-gray-500 py-3 text-center">Sin datos</p>
              ) : (
                <div className="h-56 bg-white/5 rounded p-2" data-testid="drill-top-pages-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={data.top_pages.map((p) => ({ ...p, name: HUMAN_PAGES[p.page] || p.page, minutes: Math.round((p.seconds || 0) / 60) }))}
                              margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                      <XAxis type="number" stroke="#666" fontSize={10} />
                      <YAxis dataKey="name" type="category" stroke="#aaa" fontSize={10} width={90} />
                      <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff", fontSize: 12 }} />
                      <Bar dataKey="minutes" fill="#CCFF00" name="Minutos" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {(data.top_pages || []).map((p) => (
                  <li key={p.page} className="flex items-center gap-2 text-xs text-gray-300 py-1 border-b border-white/5" data-testid={`drill-page-${p.page}`}>
                    <span className="flex-1 uppercase tracking-wider text-gray-400">{HUMAN_PAGES[p.page] || p.page}</span>
                    <span className="text-[#CCFF00] font-bold">{fmtDuration(p.seconds)}</span>
                    <span className="text-gray-500 w-16 text-right">{p.visits} visitas</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Daily activity */}
            <section>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Actividad diaria</p>
              {(data.daily || []).length === 0 ? (
                <p className="text-sm italic text-gray-500 py-3 text-center">Sin datos</p>
              ) : (
                <div className="h-44 bg-white/5 rounded p-2" data-testid="drill-daily-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.daily.map((d) => ({ date: d.date.slice(5), minutes: Math.round((d.seconds || 0) / 60) }))}
                               margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                      <XAxis dataKey="date" stroke="#666" fontSize={9} />
                      <YAxis stroke="#666" fontSize={10} />
                      <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff", fontSize: 12 }} />
                      <Line type="monotone" dataKey="minutes" stroke="#CCFF00" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TotalCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`p-2 rounded border ${accent ? "bg-[#CCFF00]/10 border-[#CCFF00]/40" : "bg-white/5 border-white/10"}`}>
      <p className="text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1">{Icon && <Icon size={12} weight="fill" className="text-[#CCFF00]" />} {label}</p>
      <p className={`text-xl font-black font-['Barlow_Condensed'] ${accent ? "text-[#CCFF00]" : "text-white"}`}>{value}</p>
    </div>
  );
}
